import React from "react";

// Status colors — chosen to read well on BOTH the light and dark boards, so
// they stay the same across themes.
const STATUS = {
  green: "#16b364",
  blue: "#3b82f6",
  cyan: "#06b6d4",
  red: "#ef4444",
  orange: "#f59e0b",
  muted: "#8a94a6",
};

// Two board palettes. Each defines the same role tokens so Board.jsx can be
// rendered with either one. LightBoard/DarkBoard pass the matching palette.
export const LIGHT = {
  name: "light",
  wrapBg: "#eef4ff", // page background
  panelBg: "#ffffff", // dual-panel card background
  border: "#e9ecef",
  borderStrong: "#dee2e6",
  text: "#495057",
  textMuted: "#6c757d",
  title: "#181c32", // title, flight no, terminal, place name
  iconBoxBg: "#0d8efd", // header icon square
  iconFill: "#ffffff", // plane icon color
  accent: "#0d8efd",
  timeColor: "#0d8efd", // TIME column
  dateColor: "#181c32", // header date
  headColor: "#0d8efd", // column headers
  subtitleColor: "#0d8efd", // airport subtitle
  codeColor: "#0d8efd", // (CODE)
  estColor: "#6c757d", // EST time
  shadow: "0 0.75rem 1.5rem rgba(18, 38, 63, 0.06)",
};

export const DARK = {
  name: "dark",
  wrapBg: "#0b1020",
  panelBg: "#131a2b",
  border: "rgba(255,255,255,0.07)",
  borderStrong: "rgba(255,255,255,0.16)",
  text: "#c7d0e0",
  textMuted: "#7a88a6",
  title: "#f8fafc",
  iconBoxBg: "#f5b921", // amber
  iconFill: "#0b1020", // dark plane on amber
  accent: "#f5b921",
  timeColor: "#f5b921", // amber TIME
  dateColor: "#f5b921", // amber date
  headColor: "#6b7a99", // muted headers
  subtitleColor: "#6b7a99", // muted subtitle
  codeColor: "#7a88a6", // muted (CODE)
  estColor: "#7a88a6",
  shadow: "0 0.75rem 1.5rem rgba(0,0,0,0.35)",
};

export const THEMES = { light: LIGHT, dark: DARK };

export const statusInfo = (status, isDep) => {
  switch (String(status || "").toLowerCase()) {
    case "scheduled":
      return { label: "ON TIME", color: STATUS.green };
    case "active":
      return isDep
        ? { label: "DEPARTED", color: STATUS.orange }
        : { label: "EN ROUTE", color: STATUS.blue };
    case "landed":
      return { label: "LANDED", color: STATUS.cyan };
    case "cancelled":
      return { label: "CANCELLED", color: STATUS.red };
    case "incident":
      return { label: "INCIDENT", color: STATUS.red };
    case "diverted":
      return { label: "DIVERTED", color: STATUS.orange };
    default:
      return { label: "SCHEDULED", color: STATUS.muted };
  }
};

export const fmtTime = (iso) => {
  if (!iso) return "--:--";
  const m = String(iso).match(/T(\d{2}):(\d{2})/);
  if (m) return `${m[1]}:${m[2]}`;
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? "--:--"
    : d.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
};

export const fmtClock = (date) =>
  date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

export const fmtDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export const parseFlight = (no) => {
  if (!no) return { carrier: "--", number: "", full: "--" };
  const raw = String(no).toUpperCase().replace(/\s+/g, "");
  const m = raw.match(/^([A-Z0-9]{2,3})(\d+)$/);
  if (!m) return { carrier: raw.slice(0, 2), number: raw.slice(2), full: raw };
  return { carrier: m[1], number: m[2], full: `${m[1]}${m[2]}` };
};

export const fmtFlight = (no) => {
  const p = parseFlight(no);
  return p.full;
};

const AIRLINE_COLORS = {
  "6E": "#3B5BDB",
  AI: "#C8102E",
  SG: "#EE1C25",
  UK: "#1A1A2E",
  I5: "#FF6200",
  IX: "#0B3D91",
  QP: "#C9A227",
  G8: "#E31837",
  "9I": "#0033A0",
  S5: "#0055A5",
};

export const airlineColor = (carrier) => {
  const c = String(carrier || "").toUpperCase();
  if (AIRLINE_COLORS[c]) return AIRLINE_COLORS[c];
  let h = 0;
  for (let i = 0; i < c.length; i++) h = (h * 31 + c.charCodeAt(i)) >>> 0;
  const hues = [210, 350, 145, 25, 265, 190];
  return "hsl(" + hues[h % hues.length] + ", 65%, 42%)";
};

export const place = (spot) => {
  if (!spot) return "--";
  const name = spot.airport || "";
  const code = spot.iata ? ` (${spot.iata})` : "";
  return `${name}${code}`.trim() || "--";
};

export const placeParts = (spot) => ({
  name: (spot && spot.airport) || "--",
  code: (spot && spot.iata) || "",
});

export const placeShort = (spot) =>
  (spot && (spot.iata || spot.airport)) || "--";

export const fmtGate = (gate) => {
  if (!gate || gate === "--") return "--";
  const g = String(gate).trim();
  if (/^g/i.test(g)) return g.toUpperCase();
  return `G${g}`;
};

export const fmtTerminal = (term) => {
  if (!term || term === "--") return "--";
  return String(term).replace(/^t/i, "").trim() || "--";
};

export const PlaneIcon = ({ size, variant = "dep", fill = "#ffffff" }) => {
  const isArr = variant === "arr";
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill={fill}
      style={
        isArr
          ? { transform: "rotate(90deg)" }
          : { transform: "rotate(-45deg)" }
      }
    >
      <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z" />
    </svg>
  );
};

export const DepArrowIcon = ({ size, fill = "#ffffff" }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill={fill}>
    <path d="M2.5 19h19v2H2.5v-2zm19.57-9.36c-.21-.8-1.04-1.28-1.84-1.06L14.92 10l-6.9-6.43-1.93.51 4.14 7.17-4.97 1.33-1.97-1.54-1.45.39 2.59 4.49 5.31-1.42 4.35-1.16L21 11.49c.81-.23 1.28-1.05 1.07-1.85z" />
  </svg>
);

export const ArrArrowIcon = ({ size, fill = "#ffffff" }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill={fill}>
    <path d="M2.5 19h19v2H2.5v-2zm16.84-3.15c.8.21 1.62-.26 1.84-1.06.21-.8-.26-1.62-1.06-1.84l-5.31-1.42-2.76-9.02L10.12 2v8.28L5.15 8.95l-.93-2.32-1.45-.39v5.17l1.6.43 5.31 1.84 4.35 1.16 4.31 1.22z" />
  </svg>
);

export const BoardPlaneIcon = ({ size, isDep, dual, fill = "#ffffff" }) => {
  if (dual) {
    return (
      <svg viewBox="0 0 24 24" width={size} height={size} fill={fill}>
        <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z" />
      </svg>
    );
  }
  return isDep ? (
    <DepArrowIcon size={size} fill={fill} />
  ) : (
    <ArrArrowIcon size={size} fill={fill} />
  );
};
